import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { VocabTagInput } from '../vocab/VocabTagInput';
import { SpeakingEntityPicker } from '../entities/SpeakingEntityPicker';
import {
  useUpdateParagraph,
  type BookDto,
  type ChapterDto,
  type ParagraphDto,
} from '../../api/queries/books';
import { resolveEffectiveMetadata } from '../../lib/metadata';
import { AmbientAudioSelector } from './AmbientAudioSelector';

interface Props {
  book: BookDto;
  chapter: ChapterDto | undefined;
  paragraph: ParagraphDto | undefined;
}

/**
 * Scene metadata drawer — mood / tone / tone_modifiers / emotional_intensity /
 * scene_description. Inherited values render faded; paragraph-level overrides render
 * bold with a "clear override" chip. Updates write straight through via
 * UpdateParagraphCommand (no speaking_entity_ids yet — that lands with Phase 2 entities).
 */
export function MetadataInheritancePanel({ book, chapter, paragraph }: Props) {
  const updateParagraph = useUpdateParagraph();
  const [mood, setMood] = useState(paragraph?.mood ?? '');
  const [tone, setTone] = useState(paragraph?.tone ?? '');
  const [emotional, setEmotional] = useState<number | null>(
    paragraph?.emotionalIntensity ?? null,
  );
  const [scene, setScene] = useState(paragraph?.sceneDescription ?? '');

  useEffect(() => {
    setMood(paragraph?.mood ?? '');
    setTone(paragraph?.tone ?? '');
    setEmotional(paragraph?.emotionalIntensity ?? null);
    setScene(paragraph?.sceneDescription ?? '');
  }, [paragraph?.id]);

  if (!paragraph) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select a paragraph to edit scene metadata.
        </Typography>
      </Paper>
    );
  }

  const effective = resolveEffectiveMetadata(
    {
      pov: book.pov,
      narrativeStyle: book.narrativeStyle,
      tense: book.tense,
      genre: book.genre,
    },
    chapter
      ? {
          povOverride: chapter.povOverride,
          narrativeStyleOverride: chapter.narrativeStyleOverride,
          tenseOverride: chapter.tenseOverride,
        }
      : null,
    {
      mood: paragraph.mood,
      tone: paragraph.tone,
      sceneDescription: paragraph.sceneDescription,
    },
  );

  const save = async (next: Partial<ParagraphDto>) => {
    await updateParagraph.mutateAsync({
      id: paragraph.id,
      contentHtml: paragraph.contentHtml,
      type: paragraph.type,
      mood: next.mood ?? mood,
      tone: next.tone ?? tone,
      emotionalIntensity: next.emotionalIntensity ?? emotional ?? undefined,
      sceneDescription: next.sceneDescription ?? scene,
    });
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Scene metadata</Typography>

        <FieldRow
          label="POV"
          effective={effective.pov}
          inherited={effective.pov.origin !== 'paragraph'}
        />
        <FieldRow
          label="Tense"
          effective={effective.tense}
          inherited={effective.tense.origin !== 'paragraph'}
        />

        <VocabTagInput
          kind="mood"
          label="Mood"
          single
          value={mood ? [mood] : []}
          onChange={(v) => {
            const next = v[0] ?? '';
            setMood(next);
            void save({ mood: next });
          }}
        />

        <VocabTagInput
          kind="tone_modifiers"
          label="Tone"
          single
          value={tone ? [tone] : []}
          onChange={(v) => {
            const next = v[0] ?? '';
            setTone(next);
            void save({ tone: next });
          }}
        />

        <TextField
          type="number"
          size="small"
          label="Emotional intensity (1–5)"
          value={emotional ?? ''}
          onChange={(e) => {
            const v =
              e.target.value === '' ? null : parseInt(e.target.value, 10);
            setEmotional(v);
            void save({ emotionalIntensity: v ?? undefined });
          }}
          inputProps={{ min: 1, max: 5 }}
        />

        <TextField
          size="small"
          label="Scene description"
          value={scene}
          onChange={(e) => setScene(e.target.value)}
          onBlur={() => void save({ sceneDescription: scene })}
          multiline
          minRows={2}
        />

        <SpeakingEntityPicker
          bookId={paragraph.bookId}
          value={paragraph.speakingEntityIds ?? []}
          onChange={(ids) =>
            void updateParagraph.mutateAsync({
              id: paragraph.id,
              contentHtml: paragraph.contentHtml,
              type: paragraph.type,
              mood: paragraph.mood,
              tone: paragraph.tone,
              emotionalIntensity: paragraph.emotionalIntensity ?? undefined,
              sceneDescription: paragraph.sceneDescription,
              speakingEntityIds: ids,
            })
          }
        />

        {(paragraph.mood || paragraph.tone || paragraph.sceneDescription) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setMood('');
              setTone('');
              setScene('');
              void save({ mood: '', tone: '', sceneDescription: '' });
            }}
          >
            Clear paragraph overrides
          </Button>
        )}

        {chapter && (
          <AmbientAudioSelector
            chapterId={chapter.id}
            onSelect={() => {
              /* ambient URL persistence is not wired to backend yet */
            }}
          />
        )}
      </Stack>
    </Paper>
  );
}

function FieldRow({
  label,
  effective,
  inherited,
}: {
  label: string;
  effective: {
    value: string | null | undefined;
    origin: 'book' | 'chapter' | 'paragraph';
  };
  inherited: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          opacity: inherited ? 0.6 : 1,
          fontWeight: inherited ? 400 : 600,
        }}
      >
        {effective.value ?? '—'}{' '}
        <Typography component="span" variant="caption" color="text.disabled">
          ({effective.origin})
        </Typography>
      </Typography>
    </Box>
  );
}
